import React, { useMemo, useState } from "react";
import { manualWorkoutsApi } from "../api/routes";
import {
  Button,
  Card,
  Input,
  JsonPreview,
  Pill,
  Select,
  TextArea,
  Toast,
} from "../components/ui/Controls";

const workoutTypeOptions = [
  "EASY_RUN",
  "TEMPO",
  "INTERVAL",
  "CROSS_TRAINING",
  "HILL",
  "TRAIL",
  "LONG_RUN",
  "RECOVERY",
  "STRENGTH",
  "OTHER",
];

const blankInterval = () => ({
  description: "",
  type: "run",
  plannedDistanceKm: "",
  plannedPaceTarget: "",
  actualDistanceKm: "",
  actualDurationSeconds: "",
  actualPace: "",
  avgHrBpm: "",
  maxHrBpm: "",
  avgCadence: "",
  elevationGainM: "",
  completed: true,
});

const blankForm = () => ({
  manualEntryId: "",
  userId: "",
  personalizedWorkoutId: "",
  personalizedWorkoutVersion: "",
  type: "TEMPO",
  name: "",
  notes: "",
  actualDistanceKm: "",
  actualPace: "",
  actualDuration: "",
  movingTimeSeconds: "",
  elapsedTimeSeconds: "",
  elevationGainM: "",
  avgHrBpm: "",
  maxHrBpm: "",
  avgCadence: "",
  caloriesKcal: "",
  startedAt: "",
  endedAt: "",
  timeBased: false,
  intervals: [blankInterval()],
});

function cleanNumber(value) {
  return value === "" || value == null ? undefined : Number(value);
}

function kilometersDistance(value, displayHint = "RAW_SOURCE") {
  const km = cleanNumber(value);
  if (km === undefined) return undefined;
  return {
    meters: Math.round(km * 1000),
    sourceValue: km,
    sourceUnit: "KILOMETER",
    displayHint,
  };
}

function sourceUnitLabel(unit) {
  if (unit === "METER") return "m";
  if (unit === "KILOMETER") return "km";
  if (unit === "MILE") return "mi";
  return "km";
}

function distanceLabel(value, fallbackKm) {
  if (value?.label) return value.label;
  if (value?.value !== undefined && value?.unit) return `${value.value} ${value.unit}`;
  if (value?.sourceValue !== undefined && value?.sourceUnit) return `${value.sourceValue} ${sourceUnitLabel(value.sourceUnit)}`;
  if (value?.meters !== undefined) return `${Number((value.meters / 1000).toFixed(2))} km`;
  if (fallbackKm) return `${fallbackKm} km`;
  return "No distance";
}

function distanceInputValue(value, fallback) {
  if (value?.value !== undefined) return value.value;
  if (value?.sourceUnit === "KILOMETER" && value?.sourceValue !== undefined) return value.sourceValue;
  if (value?.meters !== undefined) return Number((value.meters / 1000).toFixed(3));
  return fallback ?? "";
}

function serializeForm(form) {
  return {
    userId: form.userId,
    personalizedWorkoutId: form.personalizedWorkoutId || undefined,
    personalizedWorkoutVersion: cleanNumber(form.personalizedWorkoutVersion),
    type: form.type || undefined,
    name: form.name || undefined,
    notes: form.notes || undefined,
    actualDistance: kilometersDistance(form.actualDistanceKm),
    actualDistanceKm: cleanNumber(form.actualDistanceKm),
    actualPace: form.actualPace || undefined,
    actualDuration: form.actualDuration || undefined,
    movingTimeSeconds: cleanNumber(form.movingTimeSeconds),
    elapsedTimeSeconds: cleanNumber(form.elapsedTimeSeconds),
    elevationGainM: cleanNumber(form.elevationGainM),
    avgHrBpm: cleanNumber(form.avgHrBpm),
    maxHrBpm: cleanNumber(form.maxHrBpm),
    avgCadence: cleanNumber(form.avgCadence),
    caloriesKcal: cleanNumber(form.caloriesKcal),
    startedAt: form.startedAt || undefined,
    endedAt: form.endedAt || undefined,
    timeBased: form.timeBased,
    intervals: form.intervals
      .filter((interval) => interval.description || interval.actualDistanceKm || interval.actualDurationSeconds)
      .map((interval, index) => ({
        intervalIndex: index,
        description: interval.description || undefined,
        type: interval.type || undefined,
        plannedDistance: kilometersDistance(interval.plannedDistanceKm, "TRACK_REP"),
        plannedDistanceKm: cleanNumber(interval.plannedDistanceKm),
        plannedPaceTarget: interval.plannedPaceTarget || undefined,
        actualDistance: kilometersDistance(interval.actualDistanceKm, "TRACK_REP"),
        actualDistanceKm: cleanNumber(interval.actualDistanceKm),
        actualDurationSeconds: cleanNumber(interval.actualDurationSeconds),
        actualPace: interval.actualPace || undefined,
        avgHrBpm: cleanNumber(interval.avgHrBpm),
        maxHrBpm: cleanNumber(interval.maxHrBpm),
        avgCadence: cleanNumber(interval.avgCadence),
        elevationGainM: cleanNumber(interval.elevationGainM),
        completed: interval.completed,
      })),
  };
}

function normalizeEntry(entry) {
  return {
    manualEntryId: entry.manualEntryId || "",
    userId: entry.userId || "",
    personalizedWorkoutId: entry.personalizedWorkoutId || "",
    personalizedWorkoutVersion: entry.personalizedWorkoutVersion ?? "",
    type: entry.type || "TEMPO",
    name: entry.name || "",
    notes: entry.notes || "",
    actualDistanceKm: distanceInputValue(entry.actualDistance, entry.actualDistanceKm),
    actualPace: entry.actualPace || "",
    actualDuration: entry.actualDuration || "",
    movingTimeSeconds: entry.movingTimeSeconds ?? "",
    elapsedTimeSeconds: entry.elapsedTimeSeconds ?? "",
    elevationGainM: entry.elevationGainM ?? "",
    avgHrBpm: entry.avgHrBpm ?? "",
    maxHrBpm: entry.maxHrBpm ?? "",
    avgCadence: entry.avgCadence ?? "",
    caloriesKcal: entry.caloriesKcal ?? "",
    startedAt: entry.startedAt || "",
    endedAt: entry.endedAt || "",
    timeBased: Boolean(entry.timeBased),
    intervals: entry.intervals?.length
      ? entry.intervals.map((interval) => ({
          description: interval.description || "",
          type: interval.type || "run",
          plannedDistanceKm: distanceInputValue(interval.plannedDistance, interval.plannedDistanceKm),
          plannedPaceTarget: interval.plannedPaceTarget || "",
          actualDistanceKm: distanceInputValue(interval.actualDistance, interval.actualDistanceKm),
          actualDurationSeconds: interval.actualDurationSeconds ?? "",
          actualPace: interval.actualPace || "",
          avgHrBpm: interval.avgHrBpm ?? "",
          maxHrBpm: interval.maxHrBpm ?? "",
          avgCadence: interval.avgCadence ?? "",
          elevationGainM: interval.elevationGainM ?? "",
          completed: interval.completed !== false,
        }))
      : [blankInterval()],
  };
}

export default function ManualWorkoutsPanel() {
  const [form, setForm] = useState(blankForm());
  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [response, setResponse] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedEntry = useMemo(
    () => entries.find((entry) => entry.manualEntryId === selectedId) || null,
    [entries, selectedId]
  );

  const patchForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const loadEntries = async () => {
    if (!form.userId) {
      setToast({ tone: "error", message: "User ID is required to load entries." });
      return;
    }
    setLoading(true);
    try {
      const res = await manualWorkoutsApi.byUser(form.userId);
      setEntries(Array.isArray(res.data) ? res.data : []);
      setResponse(res.data);
    } catch (e) {
      setToast({ tone: "error", message: e.message });
      setResponse({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const saveEntry = async () => {
    if (!form.userId) {
      setToast({ tone: "error", message: "User ID is required before saving." });
      return;
    }
    setLoading(true);
    try {
      const payload = serializeForm(form);
      const res = form.manualEntryId
        ? await manualWorkoutsApi.update(form.userId, form.manualEntryId, payload)
        : await manualWorkoutsApi.create(payload);
      const normalized = normalizeEntry(res.data);
      setForm(normalized);
      setSelectedId(res.data.manualEntryId);
      setResponse(res.data);
      setToast({ tone: "success", message: form.manualEntryId ? "Manual workout updated." : "Manual workout created." });
      await loadEntries();
    } catch (e) {
      setToast({ tone: "error", message: e.message });
      setResponse({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async () => {
    if (!form.userId || !form.manualEntryId) {
      return;
    }
    if (!window.confirm("Delete this manual workout entry?")) {
      return;
    }
    setLoading(true);
    try {
      const res = await manualWorkoutsApi.delete(form.userId, form.manualEntryId);
      setToast({ tone: "success", message: "Manual workout deleted." });
      setResponse(res.data);
      setForm((current) => ({ ...blankForm(), userId: current.userId }));
      setSelectedId("");
      await loadEntries();
    } catch (e) {
      setToast({ tone: "error", message: e.message });
      setResponse({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const startFresh = () => {
    setSelectedId("");
    setForm((current) => ({ ...blankForm(), userId: current.userId }));
  };

  const hydrateFromEntry = (entry) => {
    setSelectedId(entry.manualEntryId);
    setForm(normalizeEntry(entry));
  };

  const updateInterval = (index, key, value) => {
    setForm((current) => ({
      ...current,
      intervals: current.intervals.map((interval, idx) =>
        idx === index ? { ...interval, [key]: value } : interval
      ),
    }));
  };

  const addInterval = () =>
    setForm((current) => ({
      ...current,
      intervals: [...current.intervals, blankInterval()],
    }));

  const removeInterval = (index) =>
    setForm((current) => ({
      ...current,
      intervals: current.intervals.filter((_, idx) => idx !== index),
    }));

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.3fr) minmax(320px, 0.7fr)",
          gap: 18,
          alignItems: "start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Card
            title="Manual Workout Studio"
            tone="accent"
            action={
              <div style={{ display: "flex", gap: 8 }}>
                <Button tone="ghost" onClick={startFresh}>New entry</Button>
                <Button tone="ghost" onClick={loadEntries} disabled={loading}>Refresh</Button>
              </div>
            }
          >
            <div
              style={{
                padding: 14,
                marginBottom: 16,
                borderRadius: 16,
                background:
                  "linear-gradient(135deg, rgba(25,164,99,0.12), rgba(12,59,46,0.08), rgba(255,255,255,0.65))",
                border: "1px solid rgba(25,164,99,0.16)",
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>
                  Capture the real session, not just the plan.
                </div>
                <div style={{ color: "var(--stone-700)", maxWidth: 520 }}>
                  Link a completed session to a planned workout, log the actual metrics, and add interval-by-interval detail when the workout deserves it.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Pill>{entries.length} loaded</Pill>
                <Pill>{selectedEntry ? "Editing" : "Draft"}</Pill>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              <Input label="User ID" value={form.userId} onChange={(value) => patchForm("userId", value)} />
              <Select label="Workout Type" value={form.type} onChange={(value) => patchForm("type", value)} options={workoutTypeOptions} />
              <Input label="Planned Workout ID" value={form.personalizedWorkoutId} onChange={(value) => patchForm("personalizedWorkoutId", value)} />
              <Input label="Planned Version" value={form.personalizedWorkoutVersion} onChange={(value) => patchForm("personalizedWorkoutVersion", value)} type="number" />
              <Input label="Session Name" value={form.name} onChange={(value) => patchForm("name", value)} />
              <Input label="Started At" value={form.startedAt} onChange={(value) => patchForm("startedAt", value)} placeholder="2026-03-23T10:00:00Z" />
              <Input label="Distance (km)" value={form.actualDistanceKm} onChange={(value) => patchForm("actualDistanceKm", value)} type="number" />
              <Input label="Actual Pace" value={form.actualPace} onChange={(value) => patchForm("actualPace", value)} placeholder="4:58/km" />
              <Input label="Actual Duration" value={form.actualDuration} onChange={(value) => patchForm("actualDuration", value)} placeholder="0:32:00" />
              <Input label="Moving Time (s)" value={form.movingTimeSeconds} onChange={(value) => patchForm("movingTimeSeconds", value)} type="number" />
              <Input label="Elapsed Time (s)" value={form.elapsedTimeSeconds} onChange={(value) => patchForm("elapsedTimeSeconds", value)} type="number" />
              <Input label="Average HR" value={form.avgHrBpm} onChange={(value) => patchForm("avgHrBpm", value)} type="number" />
              <Input label="Max HR" value={form.maxHrBpm} onChange={(value) => patchForm("maxHrBpm", value)} type="number" />
              <Input label="Cadence" value={form.avgCadence} onChange={(value) => patchForm("avgCadence", value)} type="number" />
              <Input label="Elevation Gain (m)" value={form.elevationGainM} onChange={(value) => patchForm("elevationGainM", value)} type="number" />
            </div>

            <TextArea
              label="Coach Notes / Athlete Notes"
              rows={4}
              value={form.notes}
              onChange={(value) => patchForm("notes", value)}
              placeholder="Examples: felt smooth, manual-marker, shortened cooldown, treadmill due to weather"
            />

            <div
              style={{
                borderRadius: 16,
                padding: 16,
                background: "rgba(12,59,46,0.04)",
                border: "1px solid rgba(12,59,46,0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>Intervals</div>
                  <div style={{ color: "var(--stone-700)", fontSize: 13 }}>
                    Add the pieces that mattered: reps, recoveries, climbs, tempo blocks.
                  </div>
                </div>
                <Button tone="ghost" onClick={addInterval}>Add interval</Button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {form.intervals.map((interval, index) => (
                  <div
                    key={`${index}-${selectedId || "draft"}`}
                    style={{
                      borderRadius: 14,
                      padding: 14,
                      background: "rgba(255,255,255,0.78)",
                      border: "1px solid rgba(15,23,42,0.08)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div style={{ fontWeight: 700 }}>Interval {index + 1}</div>
                      {form.intervals.length > 1 && (
                        <Button tone="ghost" onClick={() => removeInterval(index)}>Remove</Button>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
                      <Input label="Description" value={interval.description} onChange={(value) => updateInterval(index, "description", value)} />
                      <Input label="Type" value={interval.type} onChange={(value) => updateInterval(index, "type", value)} />
                      <Input label="Planned Pace" value={interval.plannedPaceTarget} onChange={(value) => updateInterval(index, "plannedPaceTarget", value)} />
                      <Input label="Planned Dist (km)" value={interval.plannedDistanceKm} onChange={(value) => updateInterval(index, "plannedDistanceKm", value)} type="number" />
                      <Input label="Actual Dist (km)" value={interval.actualDistanceKm} onChange={(value) => updateInterval(index, "actualDistanceKm", value)} type="number" />
                      <Input label="Actual Duration (s)" value={interval.actualDurationSeconds} onChange={(value) => updateInterval(index, "actualDurationSeconds", value)} type="number" />
                      <Input label="Actual Pace" value={interval.actualPace} onChange={(value) => updateInterval(index, "actualPace", value)} />
                      <Input label="Avg HR" value={interval.avgHrBpm} onChange={(value) => updateInterval(index, "avgHrBpm", value)} type="number" />
                      <Input label="Max HR" value={interval.maxHrBpm} onChange={(value) => updateInterval(index, "maxHrBpm", value)} type="number" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <Button onClick={saveEntry} disabled={loading}>
                {form.manualEntryId ? "Update workout" : "Save workout"}
              </Button>
              <Button tone="secondary" onClick={deleteEntry} disabled={!form.manualEntryId || loading}>
                Delete
              </Button>
            </div>
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Card title="Loaded Manual Workouts" action={<Pill>{entries.length}</Pill>}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 560, overflow: "auto" }}>
              {entries.length === 0 && (
                <div style={{ color: "var(--stone-700)" }}>
                  Load a user to inspect or edit their manual workout entries.
                </div>
              )}
              {entries.map((entry) => (
                <button
                  key={entry.manualEntryId}
                  onClick={() => hydrateFromEntry(entry)}
                  style={{
                    textAlign: "left",
                    border: selectedId === entry.manualEntryId
                      ? "1px solid rgba(25,164,99,0.48)"
                      : "1px solid rgba(15,23,42,0.08)",
                    background: selectedId === entry.manualEntryId
                      ? "linear-gradient(135deg, rgba(25,164,99,0.14), rgba(255,255,255,0.92))"
                      : "rgba(255,255,255,0.82)",
                    borderRadius: 14,
                    padding: 14,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
                    <div style={{ fontWeight: 800 }}>{entry.name || "Untitled workout"}</div>
                    <Pill>{entry.type || "OTHER"}</Pill>
                  </div>
                  <div style={{ color: "var(--stone-700)", fontSize: 13, marginBottom: 6 }}>
                    {distanceLabel(entry.actualDistance, entry.actualDistanceKm)} • {entry.actualPace || "No pace"} • {entry.actualDuration || "No duration"}
                  </div>
                  <div style={{ color: "var(--stone-700)", fontSize: 12 }}>
                    {entry.notes || "No notes"}
                  </div>
                </button>
              ))}
            </div>
          </Card>
          <JsonPreview data={response} label="Manual Workout Payload" />
        </div>
      </div>
    </>
  );
}
